import React, { useState, useRef, useEffect, useCallback } from 'react';
import { TeacherAvatar } from '../../components/TeacherAvatar';
import { useTeacherAvatar } from '../../hooks/useTeacherAvatar';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Slider } from '../../components/ui/slider';
import { useLabProgress } from '../../hooks/useLabProgress';

import {
  ArrowLeft, Play, RotateCcw, Target, Trophy, Zap, Atom, Star, Pause, Info
} from 'lucide-react';

const GRAVITY = 9.81;
const SCALE = 3;
const GROUND_Y = 380;
const CANNON_X = 60;
const CANNON_Y = GROUND_Y;

const MISSIONS = [
  { id: 1, name: 'First Launch', description: 'Hit any target', targetX: 300, targetWidth: 60, stars: 1 },
  { id: 2, name: 'Precision Shot', description: 'Hit the far target', targetX: 500, targetWidth: 40, stars: 2 },
  { id: 3, name: 'Trick Shot', description: 'Hit the tiny target at max range', targetX: 700, targetWidth: 30, stars: 3 },
  { id: 4, name: 'Low Angle', description: 'Hit the close target using an angle below 25°', targetX: 200, targetWidth: 50, stars: 1 },
  { id: 5, name: 'High Arc', description: 'Hit the target using an angle above 70°', targetX: 250, targetWidth: 45, stars: 2 },
  { id: 6, name: 'Maximum Range', description: 'Launch the projectile as far as possible (45° is optimal!)', targetX: 750, targetWidth: 50, stars: 2 },
  { id: 7, name: 'Double Hit', description: 'Hit this mid-range target twice in a row', targetX: 400, targetWidth: 35, stars: 2 },
  { id: 8, name: 'Speed Challenge', description: 'Hit the target with minimum velocity', targetX: 350, targetWidth: 40, stars: 2 },
  { id: 9, name: 'Sniper Shot', description: 'Hit the ultra-narrow target at mid range', targetX: 450, targetWidth: 20, stars: 3 },
  { id: 10, name: 'Impossible Shot', description: 'Hit the tiny target at extreme range', targetX: 780, targetWidth: 15, stars: 3 },
];

export default function PhysicsLab() {
  const { showTeacher, dismiss, resummon, dismissed, avatarId } = useTeacherAvatar('physics');
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const navigate = useNavigate();
  
  const [angle, setAngle] = useState(45);
  const [velocity, setVelocity] = useState(50);
  const [isLaunched, setIsLaunched] = useState(false);
  const [projectile, setProjectile] = useState(null);
  const [trail, setTrail] = useState([]);
  const [stats, setStats] = useState({ maxHeight: 0, distance: 0, time: 0 });
  const { completedMissions, unlockedMissions, currentMission, setCurrentMission, completeMission: saveProgress, xp, loading: progressLoading } = useLabProgress('physics', 10);
  const [showHit, setShowHit] = useState(false);
  const [showMiss, setShowMiss] = useState(false);

  const mission = MISSIONS[currentMission];

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;

    ctx.clearRect(0, 0, w, h);

    // Sky gradient
    const skyGrad = ctx.createLinearGradient(0, 0, 0, GROUND_Y);
    skyGrad.addColorStop(0, '#0f172a');
    skyGrad.addColorStop(1, '#1e293b');
    ctx.fillStyle = skyGrad;
    ctx.fillRect(0, 0, w, GROUND_Y);

    // Ground
    const groundGrad = ctx.createLinearGradient(0, GROUND_Y, 0, h);
    groundGrad.addColorStop(0, '#065f46');
    groundGrad.addColorStop(1, '#064e3b');
    ctx.fillStyle = groundGrad;
    ctx.fillRect(0, GROUND_Y, w, h - GROUND_Y);

    // Grid lines
    ctx.strokeStyle = 'rgba(255,255,255,0.05)';
    ctx.lineWidth = 1;
    for (let x = 0; x < w; x += 50) {
      ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, GROUND_Y); ctx.stroke();
    }
    for (let y = 0; y < GROUND_Y; y += 50) {
      ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke();
    }

    // Distance markers
    ctx.fillStyle = 'rgba(255,255,255,0.3)';
    ctx.font = '10px monospace';
    for (let d = 100; d < w; d += 100) {
      ctx.fillText(`${d}m`, d, GROUND_Y + 15);
      ctx.beginPath();
      ctx.moveTo(d, GROUND_Y);
      ctx.lineTo(d, GROUND_Y + 5);
      ctx.strokeStyle = 'rgba(255,255,255,0.3)';
      ctx.stroke();
    }

    // Target
    if (mission) {
      const tx = mission.targetX;
      const tw = mission.targetWidth;
      // Target pole
      ctx.fillStyle = '#ef4444';
      ctx.fillRect(tx + tw / 2 - 2, GROUND_Y - 50, 4, 50);
      // Target circle
      ctx.beginPath();
      ctx.arc(tx + tw / 2, GROUND_Y - 50, tw / 2, 0, Math.PI * 2);
      ctx.fillStyle = '#ef4444';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(tx + tw / 2, GROUND_Y - 50, tw / 3, 0, Math.PI * 2);
      ctx.fillStyle = '#fff';
      ctx.fill();
      ctx.beginPath();
      ctx.arc(tx + tw / 2, GROUND_Y - 50, tw / 6, 0, Math.PI * 2);
      ctx.fillStyle = '#ef4444';
      ctx.fill();
    }

    // Cannon base
    ctx.fillStyle = '#475569';
    ctx.beginPath();
    ctx.arc(CANNON_X, CANNON_Y, 18, Math.PI, 0);
    ctx.fill();

    // Cannon barrel
    const rad = (angle * Math.PI) / 180;
    const barrelLen = 40;
    ctx.save();
    ctx.translate(CANNON_X, CANNON_Y);
    ctx.rotate(-rad);
    ctx.fillStyle = '#64748b';
    ctx.fillRect(0, -5, barrelLen, 10);
    ctx.fillStyle = '#94a3b8';
    ctx.fillRect(barrelLen - 5, -6, 5, 12);
    ctx.restore();

    // Trail
    if (trail.length > 1) {
      ctx.beginPath();
      ctx.moveTo(trail[0].x, trail[0].y);
      for (let i = 1; i < trail.length; i++) {
        ctx.lineTo(trail[i].x, trail[i].y);
      }
      ctx.strokeStyle = 'rgba(250,204,21,0.4)';
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 4]);
      ctx.stroke();
      ctx.setLineDash([]);

      // Trail dots
      trail.forEach((p, i) => {
        if (i % 3 === 0) {
          ctx.beginPath();
          ctx.arc(p.x, p.y, 2, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(250,204,21,${0.3 + (i / trail.length) * 0.7})`;
          ctx.fill();
        }
      });
    }

    // Projectile
    if (projectile) {
      // Glow
      const glow = ctx.createRadialGradient(projectile.x, projectile.y, 0, projectile.x, projectile.y, 15);
      glow.addColorStop(0, 'rgba(250,204,21,0.6)');
      glow.addColorStop(1, 'rgba(250,204,21,0)');
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.arc(projectile.x, projectile.y, 15, 0, Math.PI * 2);
      ctx.fill();
      // Ball
      ctx.beginPath();
      ctx.arc(projectile.x, projectile.y, 6, 0, Math.PI * 2);
      ctx.fillStyle = '#facc15';
      ctx.fill();
      ctx.strokeStyle = '#eab308';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }

    // Hit/Miss effects
    if (showHit) {
      ctx.font = 'bold 28px sans-serif';
      ctx.fillStyle = '#22c55e';
      ctx.textAlign = 'center';
      ctx.fillText('TARGET HIT!', w / 2, 60);
      ctx.font = '16px sans-serif';
      ctx.fillStyle = '#86efac';
      ctx.fillText('Great shot!', w / 2, 85);
    }
    if (showMiss) {
      ctx.font = 'bold 24px sans-serif';
      ctx.fillStyle = '#f87171';
      ctx.textAlign = 'center';
      ctx.fillText('MISSED! Try again', w / 2, 60);
    }

    // Angle arc preview
    if (!isLaunched) {
      ctx.beginPath();
      ctx.arc(CANNON_X, CANNON_Y, 55, -rad, 0);
      ctx.strokeStyle = 'rgba(250,204,21,0.3)';
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = '#facc15';
      ctx.font = '11px monospace';
      ctx.textAlign = 'left';
      ctx.fillText(`${angle}°`, CANNON_X + 60, CANNON_Y - 8);
    }
  }, [angle, projectile, trail, mission, isLaunched, showHit, showMiss]);

  useEffect(() => {
    draw();
  }, [draw]);

  const launch = () => {
    if (isLaunched) return;
    setIsLaunched(true);
    setShowHit(false);
    setShowMiss(false);
    setTrail([]);

    const rad = (angle * Math.PI) / 180;
    const vx = velocity * Math.cos(rad) * SCALE;
    const vy = velocity * Math.sin(rad) * SCALE;
    let t = 0;
    let maxH = 0;
    const newTrail = [];

    const step = () => {
      t += 0.02;
      const x = CANNON_X + vx * t;
      const y = CANNON_Y - (vy * t - 0.5 * GRAVITY * SCALE * t * t);
      const height = CANNON_Y - y;
      if (height > maxH) maxH = height;

      newTrail.push({ x, y });
      setTrail([...newTrail]);
      setProjectile({ x, y });
      setStats({
        maxHeight: Math.max(0, maxH / SCALE).toFixed(1),
        distance: Math.max(0, (x - CANNON_X) / SCALE).toFixed(1),
        time: t.toFixed(2),
      });

      // Check ground collision
      if (y >= GROUND_Y && t > 0.1) {
        setProjectile({ x, y: GROUND_Y });
        setIsLaunched(false);

        // Check target hit
        if (mission) {
          const hitX = x;
          const tCenter = mission.targetX + mission.targetWidth / 2;
          const hitRadius = mission.targetWidth;
          if (Math.abs(hitX - tCenter) < hitRadius) {
            setShowHit(true);
            if (!completedMissions.includes(currentMission)) {
              saveProgress(currentMission);
            }
          } else {
            setShowMiss(true);
          }
        }
        return;
      }

      // Out of bounds
      if (x > 850 || y < -50) {
        setIsLaunched(false);
        setShowMiss(true);
        return;
      }

      animRef.current = requestAnimationFrame(step);
    };

    animRef.current = requestAnimationFrame(step);
  };

  const reset = () => {
    if (animRef.current) cancelAnimationFrame(animRef.current);
    setIsLaunched(false);
    setProjectile(null);
    setTrail([]);
    setStats({ maxHeight: 0, distance: 0, time: 0 });
    setShowHit(false);
    setShowMiss(false);
  };

  useEffect(() => {
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="physics-lab">
      <TeacherAvatar visible={showTeacher} avatarId={avatarId}
        text="Physics lab about projectile motion, gravity, trajectory, and forces. Students launch projectiles, adjust angle and force, and observe real-time physics."
        cacheKey="lab-intro-physics"
        onClose={dismiss} dismissed={dismissed} onResummon={resummon} />
      {/* Header */}
      <header className="border-b border-border/50 bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/labs')} data-testid="back-to-labs">
              <ArrowLeft className="w-4 h-4 mr-2" /> Labs
            </Button>
            <div className="h-6 w-px bg-border" />
            <div className="flex items-center gap-2">
              <Atom className="w-5 h-5 text-blue-400" />
              <span className="font-semibold">Projectile Motion Lab</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {MISSIONS.map((m, i) => (
              <Badge
                key={m.id}
                variant={completedMissions.includes(i) ? 'default' : currentMission === i ? 'secondary' : 'outline'}
                className={`cursor-pointer text-xs ${currentMission === i ? 'ring-2 ring-primary/50' : ''}`}
                onClick={() => { reset(); setCurrentMission(i); }}
                data-testid={`mission-${m.id}`}
              >
                {completedMissions.includes(i) ? <Star className="w-3 h-3 mr-1 fill-yellow-400 text-yellow-400" /> : null}
                Mission {m.id}
              </Badge>
            ))}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Mission info */}
        <motion.div
          key={currentMission}
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex items-center gap-3 mb-4 p-3 rounded-xl bg-blue-500/10 border border-blue-500/20"
        >
          <Target className="w-5 h-5 text-blue-400 shrink-0" />
          <div>
            <span className="font-semibold text-sm">{mission.name}:</span>
            <span className="text-sm text-muted-foreground ml-2">{mission.description}</span>
          </div>
          <div className="ml-auto flex gap-1">
            {[...Array(mission.stars)].map((_, i) => (
              <Star key={i} className={`w-4 h-4 ${completedMissions.includes(currentMission) ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/30'}`} />
            ))}
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Canvas */}
          <div className="lg:col-span-3">
            <div className="rounded-2xl overflow-hidden border border-border/50 bg-card">
              <canvas
                ref={canvasRef}
                width={840}
                height={440}
                className="w-full"
                data-testid="physics-canvas"
              />
            </div>
          </div>

          {/* Controls */}
          <div className="space-y-4">
            {/* Angle */}
            <div className="p-4 rounded-xl bg-card border border-border/50">
              <label className="text-sm font-medium flex items-center justify-between mb-3">
                <span>Angle</span>
                <span className="text-primary font-mono">{angle}°</span>
              </label>
              <Slider
                value={[angle]}
                onValueChange={v => { if (!isLaunched) setAngle(v[0]); }}
                min={5}
                max={85}
                step={1}
                data-testid="angle-slider"
              />
            </div>

            {/* Velocity */}
            <div className="p-4 rounded-xl bg-card border border-border/50">
              <label className="text-sm font-medium flex items-center justify-between mb-3">
                <span>Velocity</span>
                <span className="text-primary font-mono">{velocity} m/s</span>
              </label>
              <Slider
                value={[velocity]}
                onValueChange={v => { if (!isLaunched) setVelocity(v[0]); }}
                min={10}
                max={100}
                step={1}
                data-testid="velocity-slider"
              />
            </div>

            {/* Buttons */}
            <div className="flex gap-2">
              <Button
                className="flex-1"
                onClick={launch}
                disabled={isLaunched}
                data-testid="launch-btn"
              >
                <Play className="w-4 h-4 mr-2" /> Launch
              </Button>
              <Button
                variant="outline"
                onClick={reset}
                data-testid="reset-btn"
              >
                <RotateCcw className="w-4 h-4" />
              </Button>
            </div>

            {/* Stats */}
            <div className="p-4 rounded-xl bg-card border border-border/50 space-y-3">
              <h4 className="text-sm font-semibold flex items-center gap-2">
                <Zap className="w-4 h-4 text-yellow-400" /> Flight Data
              </h4>
              <div className="grid grid-cols-1 gap-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Max Height</span>
                  <span className="font-mono">{stats.maxHeight} m</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Distance</span>
                  <span className="font-mono">{stats.distance} m</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Time</span>
                  <span className="font-mono">{stats.time} s</span>
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="p-4 rounded-xl bg-muted/30 border border-border/30">
              <h4 className="text-xs font-semibold flex items-center gap-1 mb-2 text-muted-foreground">
                <Info className="w-3 h-3" /> Physics
              </h4>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Projectiles follow a parabolic path under gravity (g = 9.81 m/s²). 
                A 45° angle gives maximum range. Higher velocity = farther distance.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
